import { useState } from 'react'
import { AlertCircle, X } from 'lucide-react'
import type { DocumentReviewIncompleteReason } from '@/features/resident/types/domain'

export interface RejectionReasonModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (reason: string) => void
  documentName: string
  predefinedReasons?: DocumentReviewIncompleteReason[]
}

export function RejectionReasonModal({
  isOpen,
  onClose,
  onSubmit,
  documentName,
  predefinedReasons = [],
}: RejectionReasonModalProps) {
  const [selectedLabel, setSelectedLabel] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [isCustom, setIsCustom] = useState(false)

  if (!isOpen) return null

  const handleClose = () => {
    setSelectedLabel('')
    setCustomReason('')
    setIsCustom(false)
    onClose()
  }

  const handleSubmit = () => {
    const reason = isCustom ? customReason.trim() : selectedLabel
    if (reason) {
      onSubmit(reason)
      handleClose()
    }
  }

  const selectReason = (label: string) => {
    setSelectedLabel(label)
    setIsCustom(false)
  }

  const selectCustom = () => {
    setIsCustom(true)
    setSelectedLabel('')
  }

  const isValid = isCustom ? customReason.trim().length > 0 : selectedLabel.length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2.5">
            <AlertCircle size={17} className="flex-shrink-0 text-red-500" />
            <h2 className="text-sm font-semibold text-gray-900">Mark Document Incomplete</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          <p className="text-xs text-gray-500">
            Marking incomplete: <span className="font-medium text-gray-800">{documentName}</span>
          </p>

          <p className="pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            Select a reason
          </p>

          <div className="space-y-2">
            {predefinedReasons.map((reason) => {
              const active = !isCustom && selectedLabel === reason.label
              return (
                <button
                  key={reason.label}
                  type="button"
                  onClick={() => selectReason(reason.label)}
                  className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    active
                      ? 'border-red-400 bg-red-50 ring-1 ring-red-200'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <p className={`text-xs font-medium ${active?"leading-snug text-red-800":"text-gray-900"}`}>{reason.label}</p>
                  {reason.description ? (
                    <p className={`mt-1 text-[11px] leading-snug ${active?"text-red-500":"text-gray-500"}`}>{reason.description}</p>
                  ) : null}
                </button>
              )
            })}
          </div>

          <button
            type="button"
            onClick={selectCustom}
            className={`w-full rounded-lg border px-3 py-2.5 text-left text-xs font-medium transition-colors ${
              isCustom
                ? 'border-red-400 bg-red-50 ring-1 ring-red-200 text-red-600'
                : 'border-dashed border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
            }`}
          >
            <p className={`text-xs font-medium ${isCustom?"leading-snug text-red-800":"text-gray-900"}`}>Other</p>
            <p className={`mt-1 text-[11px] leading-snug ${isCustom?"text-red-500":"text-gray-500"}`}>Specify a custom reason</p>
          </button>

          {isCustom ? (
            <textarea
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Enter a custom reason..."
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-900 placeholder:text-gray-400 focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-200"
            />
          ) : null}
        </div>

        <div className="flex flex-shrink-0 gap-2 border-t border-gray-200 px-5 py-4">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid}
            className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
          >
            Mark Incomplete
          </button>
        </div>
      </div>
    </div>
  )
}
